/**
 * Administration — two planes on one page.
 *
 * The shape is taken from `ai-security-ops`: a **platform plane** visible only to a platform
 * administrator, above a **tenant plane** any tenant administrator sees, with the boundary between
 * them stated on the page rather than left to be inferred.
 *
 * # The conditional rendering here is a courtesy, not a control
 *
 * `/api/v1/platform/tenants` returns 403 to anyone without `platform_administer`, whatever this
 * component decides to draw. Hiding a section protects nobody who can use `curl`, so the section
 * is hidden for tidiness and the route is what actually refuses.
 *
 * # What the platform plane deliberately cannot show
 *
 * Counts and status. Not a ticket title, not a hostname, not an audit detail — the underlying
 * database functions return aggregates and cannot be made to return anything else. That is worth
 * saying **on the page**, because an operator who does not know the limit will assume the tool is
 * broken when they cannot drill in, and go looking for a way around it.
 *
 * Authority over the estate is not authority over what is inside it.
 */

import { useCallback, useState, type JSX } from 'react';

import { api, type PrincipalView } from '../shared/api/client';
import {
  Badge,
  Card,
  ErrorBox,
  Formula,
  PageHeader,
  Spinner,
  Stat,
  StatRow,
  Table,
  type Tone,
} from '../shared/ui/ui';
import { useAsync } from '../shared/ui/useAsync';

/** Every role, with what it is for. Shown so a grant is a decision rather than a guess. */
const ROLES: { id: string; label: string; description: string; caution?: boolean }[] = [
  { id: 'viewer', label: 'Viewer', description: 'Read the estate. Changes nothing.' },
  { id: 'engineer', label: 'Engineer', description: 'Works tickets and raises access requests.' },
  {
    id: 'approver',
    label: 'Approver',
    description: 'The provider half of dual approval. Never sufficient alone.',
  },
  {
    id: 'client_approver',
    label: 'Client approver',
    description:
      "The client's half, and the only role that holds it. Also pins the contract key.",
    caution: true,
  },
  {
    id: 'tenant_admin',
    label: 'Tenant admin',
    description: 'Administers this tenant: people, roles, SLA policy, configuration items.',
    caution: true,
  },
  {
    id: 'platform_admin',
    label: 'Platform admin',
    description: 'Authority across every tenant on the deployment. Grant this almost never.',
    caution: true,
  },
];

const STATUS_TONE: Record<string, Tone> = {
  active: 'ok',
  suspended: 'warn',
  disabled: 'muted',
  provisioning: 'info',
  offboarding: 'muted',
};

export function Administration(): JSX.Element {
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => {
    setRevision((r) => r + 1);
  }, []);

  const me = useAsync(() => api.whoami(), []);
  const roles = me.data?.roles ?? [];
  const isPlatformAdmin = roles.includes('platform_admin');
  const isTenantAdmin = isPlatformAdmin || roles.includes('tenant_admin');

  if (me.loading) return <Spinner />;

  if (!isTenantAdmin) {
    return (
      <>
        <PageHeader title="Administration" subtitle="Managing people and roles needs the tenant administration role." />
        <Card>
          <p>
            You need the <Badge tone="info">tenant_admin</Badge> role to manage people and roles.
            You are signed in with{' '}
            {roles.length === 0 ? (
              <Badge tone="muted">no roles</Badge>
            ) : (
              roles.map((r) => (
                <Badge key={r} tone="muted">
                  {r}
                </Badge>
              ))
            )}
            .
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Administration"
        subtitle={
          isPlatformAdmin
            ? 'The deployment, and this tenant. Two planes — the platform view shows how large each tenant is, never what is inside it.'
            : 'People and roles in this tenant.'
        }
      />

      {/* The platform plane. Rendered only for a platform administrator — and the route refuses
          everyone else regardless, which is the check that actually matters. */}
      {isPlatformAdmin && <PlatformPlane revision={revision} />}

      <TenantPlane
        revision={revision}
        onChange={refresh}
        selfId={me.data?.principal_id ?? ''}
        canGrantPlatformAdmin={isPlatformAdmin}
      />

      {!isPlatformAdmin && (
        <Card>
          <p className="form__hint">
            You are a <Badge tone="info">tenant administrator</Badge>, so this view is scoped to
            your own tenant. Managing tenants across the deployment is reserved for a platform
            administrator.
          </p>
        </Card>
      )}
    </>
  );
}

/** Every tenant on the deployment. Counts and status only. */
function PlatformPlane({ revision }: { revision: number }): JSX.Element {
  const { data, error, loading } = useAsync(() => api.platform(), [revision]);

  if (loading) return <Spinner />;
  if (error !== null) return <ErrorBox error={error} />;
  if (data === null) return <></>;

  const t = data.totals;

  return (
    <>
      <Card
        title="The deployment"
        subtitle="Across every tenant. These are counts — this view cannot show what is inside any of them."
      >
        <StatRow>
          <Stat label="Tenants" value={t.tenants} hint={`${String(t.active_tenants)} active`} />
          <Stat label="Principals" value={t.principals} />
          <Stat label="Open tickets" value={t.open_tickets} />
        </StatRow>
        <StatRow>
          <Stat
            label="Live contracts"
            value={t.live_contracts}
            tone={t.live_contracts > 0 ? 'info' : 'neutral'}
            hint="somebody can reach a client estate right now"
          />
          <Stat
            label="Silent collectors"
            value={t.stale_collectors}
            tone={t.stale_collectors > 0 ? 'danger' : 'ok'}
            hint="drift stops being detected, anywhere"
          />
          <Stat
            label="Never onboarded"
            value={t.unpinned_tenants}
            tone={t.unpinned_tenants > 0 ? 'warn' : 'ok'}
            hint="no contract key pinned, so no session can open at all"
          />
        </StatRow>
      </Card>

      <Card
        title="Tenants"
        subtitle="Size and health. Opening a tenant's records is not possible from here, by design — a platform administrator has authority over the estate, not over what is inside it."
      >
        <Table
          columns={['Tenant', 'Status', 'People', 'Tickets', 'Estate', 'Access', 'Last activity']}
          empty="No tenants on this deployment."
        >
          {data.tenants.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="table__strong">{row.display_name}</div>
                <Formula>{row.slug}</Formula>
              </td>
              <td>
                <Badge tone={STATUS_TONE[row.status] ?? 'muted'}>{row.status}</Badge>
                {row.isolation_tier !== 'standard' && (
                  <Badge tone="info">{row.isolation_tier}</Badge>
                )}
                {/* The number that says an onboarding was never finished. Such a tenant cannot
                    open a session at all, and nothing else on this row reveals it. */}
                {!row.contract_key_pinned && <Badge tone="warn">no contract key</Badge>}
              </td>
              <td>{row.principals}</td>
              <td>
                {row.open_tickets} open
                <Formula>{row.tickets} total</Formula>
              </td>
              <td>
                {row.configuration_items} CIs
                <Formula>{row.collectors} collectors</Formula>
              </td>
              <td>
                {row.live_contracts > 0 ? (
                  <Badge tone="info">{row.live_contracts} live</Badge>
                ) : (
                  <Badge tone="muted">none</Badge>
                )}
              </td>
              <td>
                <Formula>
                  {row.last_activity_at === null
                    ? 'never'
                    : row.last_activity_at.replace('T', ' ').slice(0, 16)}
                </Formula>
              </td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

/** People and roles in the caller's own tenant. */
function TenantPlane({
  revision,
  onChange,
  selfId,
  canGrantPlatformAdmin,
}: {
  revision: number;
  onChange: () => void;
  selfId: string;
  canGrantPlatformAdmin: boolean;
}): JSX.Element {
  const { data, error, loading } = useAsync(() => api.principals(200), [revision]);
  const [writeError, setWriteError] = useState<unknown>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const rows = data ?? [];

  const act = (fn: Promise<unknown>): void => {
    setWriteError(null);
    void fn
      .then(() => {
        setEditing(null);
        onChange();
      })
      .catch((e: unknown) => {
        setWriteError(e);
      });
  };

  return (
    <Card
      title="People"
      subtitle="Roles decide what somebody may do. The two halves of dual approval are separate roles deliberately: one principal holding both would make it a formality performed by one party."
    >
      <ErrorBox error={error} />
      <ErrorBox error={writeError} />

      {loading ? (
        <Spinner />
      ) : (
        <Table
          columns={['Who', 'Kind', 'Status', 'Roles', '']}
          empty="Nobody in this tenant yet."
        >
          {rows.map((p) => (
            <tr key={p.id}>
              <td>
                {/* Rendered as text. A login is chosen at creation and is untrusted. */}
                <div className="table__strong">{p.display_name}</div>
                <Formula>{p.login}</Formula>
              </td>
              <td>
                <Badge tone={p.kind === 'human' ? 'muted' : 'info'}>{p.kind}</Badge>
              </td>
              <td>
                <Badge tone={STATUS_TONE[p.status] ?? 'muted'}>{p.status}</Badge>
                {p.must_change_password && <Formula>must change password</Formula>}
                {p.locked_until !== null && (
                  <Badge tone="warn">locked until {p.locked_until.slice(11, 16)}</Badge>
                )}
              </td>
              <td>
                {p.roles.length === 0 ? (
                  <Badge tone="muted">none</Badge>
                ) : (
                  p.roles.map((r) => (
                    <Badge key={r} tone={r === 'platform_admin' ? 'danger' : 'muted'}>
                      {r}
                    </Badge>
                  ))
                )}
              </td>
              <td>
                <button
                  type="button"
                  className="button button--small"
                  onClick={() => {
                    setEditing(editing === p.id ? null : p.id);
                  }}
                >
                  {editing === p.id ? 'cancel' : 'roles'}
                </button>
                {p.status === 'active' && p.id !== selfId && (
                  <button
                    type="button"
                    className="button button--small"
                    onClick={() => {
                      act(api.setPrincipalStatus(p.id, 'suspended'));
                    }}
                  >
                    suspend
                  </button>
                )}
                {p.status !== 'active' && (
                  <button
                    type="button"
                    className="button button--small"
                    onClick={() => {
                      act(api.setPrincipalStatus(p.id, 'active'));
                    }}
                  >
                    reinstate
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {editing !== null && (
        <RoleEditor
          principal={rows.find((p) => p.id === editing)}
          canGrantPlatformAdmin={canGrantPlatformAdmin}
          onApply={(roles) => {
            act(api.setPrincipalRoles(editing, roles));
          }}
          onCancel={() => {
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
}

/**
 * The role editor.
 *
 * Sends the COMPLETE set, matching the API. A replace rather than a grant/revoke pair is what lets
 * an administrator check the result against what is on screen — a sequence of grants and revokes
 * has an intermediate state, and the intermediate state of a role change is where somebody briefly
 * holds both halves of a dual approval.
 */
function RoleEditor({
  principal,
  canGrantPlatformAdmin,
  onApply,
  onCancel,
}: {
  principal: PrincipalView | undefined;
  canGrantPlatformAdmin: boolean;
  onApply: (roles: string[]) => void;
  onCancel: () => void;
}): JSX.Element {
  const [selected, setSelected] = useState<string[]>(principal?.roles ?? []);

  if (principal === undefined) return <></>;

  const toggle = (id: string): void => {
    setSelected((current) =>
      current.includes(id) ? current.filter((r) => r !== id) : [...current, id],
    );
  };

  const bothHalves =
    selected.includes('client_approver') &&
    (selected.includes('approver') || selected.includes('tenant_admin'));

  return (
    <div className="form">
      <p className="form__label">Roles for {principal.display_name}</p>

      <fieldset className="choice">
        <legend className="visually-hidden">Roles</legend>
        {ROLES.map((role) => {
          const blocked = role.id === 'platform_admin' && !canGrantPlatformAdmin;
          return (
            <label
              key={role.id}
              className={
                selected.includes(role.id) ? 'choice__item choice__item--on' : 'choice__item'
              }
            >
              <input
                type="checkbox"
                checked={selected.includes(role.id)}
                disabled={blocked}
                onChange={() => {
                  toggle(role.id);
                }}
              />
              <span className="choice__body">
                <span className="choice__label">
                  {role.label}
                  {role.caution === true && <Badge tone="warn">elevated</Badge>}
                </span>
                <span className="choice__description">
                  {blocked
                    ? 'Only a platform administrator can grant platform administration.'
                    : role.description}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      {bothHalves && (
        <p className="notice" role="status">
          This principal would hold <strong>both halves of dual approval</strong>. The server
          refuses to let one person approve both sides of a request, so this does not create a
          bypass &mdash; but it does mean the pool of independent approvers is smaller than it
          looks.
        </p>
      )}

      <p className="form__hint">
        Applying ends this person&rsquo;s live sessions. Roles are read at sign-in, so a removed
        role would otherwise stay usable until they next signed in &mdash; which, for a long-lived
        session, may be never.
      </p>

      <div>
        <button
          type="button"
          className="button"
          onClick={() => {
            onApply(selected);
          }}
        >
          Apply roles
        </button>
        <button type="button" className="button button--small" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
